/**
 * Doors and drawers ("fronts"): a modifier fitted into the openings of any family (research/taxonomy.md
 * §1.13: the carcass does not change). Every family hands its finished carcass parts and openings to
 * `buildFronts`, which lays out the fronts and returns their boards, joints, hardware and issues.
 *
 * A front covers one opening or a rectangle of openings (`FrontSpec.openings`, anchor first):
 * - `door`: one leaf or a `pair`, hinged `left` or `right`; `overlay` on concealed cup hinges, or `inset`
 *   flush inside the opening on butt hinges;
 * - `glazed`: the same, as a frame (two stiles, two rails, mortise and tenon) holding toughened glass in a rebate;
 * - `sliding`: two panels on a surface-mounted track in front of the carcass, over a horizontal run;
 * - `drawers`: 1–4 drawers stacked in one opening, each a plywood box on ball-bearing side runners with an
 *   applied front (overlay or inset like a door).
 *
 * Overlay: each edge of the covered rectangle grows by t − reveal over an outer board, (t − reveal)/2 over a
 * board shared with the next cell, and 0 where no board bounds it; the front stands proud of the carcass
 * (z = D … D + tf), so the overall depth grows by tf (`depthAdd`). Inset: the opening less the gap, flush
 * (z = D − tf … D). Rules are in fronts-profile.json. Pure; everything is in 0.1 mm ticks except the inputs'
 * part placements and opening bounds (mm, as in a BuildModel).
 */
import { z } from 'zod';
import profileJson from './fronts-profile.json' with { type: 'json' };
import type { Hardware, Issue, JointKind } from './assembly';
import type { ParameterDef } from './types';

export type FrontKind = 'door' | 'glazed' | 'sliding' | 'drawers';
export type FrontMount = 'overlay' | 'inset';
export type Hinge = 'left' | 'right' | 'pair';
export type Pull = 'knob' | 'bar' | 'none';

export const FrontSpecSchema = z.object({
  openings: z.array(z.string().min(1)).min(1),
  kind: z.enum(['door', 'glazed', 'sliding', 'drawers']),
  mount: z.enum(['overlay', 'inset']).default('overlay'),
  hinge: z.enum(['left', 'right', 'pair']).optional(),
  drawers: z.int().min(1).max(4).optional(),
  drawerRatios: z.array(z.number().positive()).optional(),
  pull: z.enum(['knob', 'bar', 'none']).default('knob'),
});
export type FrontSpec = z.input<typeof FrontSpecSchema>;
type Spec = z.output<typeof FrontSpecSchema>;

const ProfileSchema = z.object({
  overlayRevealMm: z.number(), insetGapMm: z.number(),
  hinges: z.object({ bands: z.array(z.object({ upToMm: z.number(), count: z.int() })), fromEndMm: z.number(), cupBoreMm: z.number(), cupFromEdgeMm: z.number(), cup: z.string(), butt: z.string() }),
  runners: z.object({ clearanceMm: z.number(), lengthsMm: z.array(z.number()), spec: z.string() }),
  drawerBox: z.object({ thicknessMm: z.number(), material: z.string(), topClearanceMm: z.number(), bottomClearanceMm: z.number(), minHeightMm: z.number(), minInnerDepthMm: z.number() }),
  glazed: z.object({ stileMm: z.number(), railMm: z.number(), rebateMm: z.number(), glassThicknessMm: z.number(), glass: z.string(), minClearMm: z.number() }),
  sliding: z.object({ overlapMm: z.number(), trackGapMm: z.number(), track: z.string() }),
  pulls: z.object({ knob: z.string(), bar: z.string(), fromEdgeMm: z.number() }),
}).passthrough();
export type FrontsProfile = z.infer<typeof ProfileSchema>;
export const FRONTS_PROFILE: FrontsProfile = Object.freeze(ProfileSchema.parse(profileJson)) as FrontsProfile;
export const FRONTS_PROFILE_VERSION = `fronts-rules@${(profileJson as { version: string }).version}`;

/** The roles fronts add (none starts with `back` or `shelf`, which the viewer matches by prefix). */
export const FRONT_ROLES = new Set(['door', 'door-stile', 'door-rail', 'glass', 'slider', 'drawer-front', 'drawer-side', 'drawer-rear', 'drawer-bottom']);
/** Joint kinds that move: they hold a part across a running gap (inset gaps, runner clearance). */
export const MOVING_JOINTS = new Set<JointKind>(['hinge', 'runner', 'track']);

/** Fronts of a design: grid keeps them at the top level, the other families in `params` (absent = none). */
export function frontsOf(d: { fronts?: unknown; params?: Record<string, unknown> | null }): Spec[] {
  const raw = d.params ? d.params.fronts : d.fronts;
  if (!Array.isArray(raw)) return [];
  const out: Spec[] = [];
  for (const f of raw) { const r = FrontSpecSchema.safeParse(f); if (r.success) out.push(r.data); }
  return out;
}

/** Stock a front board is cut from: the carcass stock, the drawer-box ply, the back sheet (drawer bottoms) or glass to order. */
export type FrontStock = 'carcass' | 'drawer' | 'back' | 'glass';

export interface FrontBoard {
  id: string;
  role: string;
  label: string;
  l: number; w: number; t: number;
  grainWorld: 'x' | 'y' | 'z';
  box: { x: number; y: number; z: number; sx: number; sy: number; sz: number };
  stock: FrontStock;
  machining?: string;
  faceSide?: 'out' | 'up';
}
export interface FrontJoint { a: string; b: string; kind: JointKind; face: string; datum: number }

export interface FrontsInput {
  /** The carcass parts, placements in mm (a BuildModel's parts). */
  parts: readonly { id: string; role: string; placement: { x: number; y: number; z: number; sx: number; sy: number; sz: number } }[];
  /** The openings, bounds in mm. */
  openings: readonly { id: string; label: string; bounds: { x0: number; x1: number; y0: number; y1: number } }[];
  /** Overall W and D (ticks), front thickness tf (the carcass stock, ticks), back-sheet thickness (ticks, for the bottoms). */
  W: number; D: number; tf: number; backT: number;
  specs: readonly FrontSpec[];
  profile?: FrontsProfile;
}

export interface FrontsOutput {
  boards: FrontBoard[];
  joints: FrontJoint[];
  hardware: Hardware[];
  errors: Issue[];
  warnings: Issue[];
  /** How far the fronts stand proud of the carcass (ticks): the overall depth is D + depthAdd. */
  depthAdd: number;
  /** Opening id → the kind of front over it (for the props and the elevation). */
  covered: Record<string, FrontKind>;
  /** The specs actually built (unknown openings pruned). */
  specs: Spec[];
}

const T = (mm: number) => Math.round(mm * 10);
type Rect = { x0: number; x1: number; y0: number; y1: number };
interface Edge { id: string | null; t: number; outer: boolean }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * The boards bounding a rectangle (ticks) on each side, read from the carcass parts: a vertical board whose
 * face meets the left or right edge across the rectangle's height, a horizontal one meeting the bottom or top.
 * `outer` when the board is at the piece's outline (the overlay covers it to its outer edge).
 */
function edgesOf(r: Rect, boxes: { id: string; role: string; b: Rect & { z0: number; z1: number } }[], openings: readonly Rect[], D: number): Record<'l' | 'r' | 'b' | 't', Edge> {
  const tol = 6; // 0.6 mm
  const overlapY = (b: Rect) => Math.min(b.y1, r.y1) - Math.max(b.y0, r.y0) > (r.y1 - r.y0) * 0.5;
  const overlapX = (b: Rect) => Math.min(b.x1, r.x1) - Math.max(b.x0, r.x0) > (r.x1 - r.x0) * 0.5;
  const pick = (cands: typeof boxes): Edge => {
    // a board a front can hang from or a runner fix to reaches the front of the piece (not a back block)
    const c = cands.filter((q) => !/^back|^cellback/.test(q.id) && q.role !== 'back' && q.b.z1 >= D - tol)[0];
    return c ? { id: c.id, t: 0, outer: false } : { id: null, t: 0, outer: false };
  };
  const verticals = boxes.filter((q) => q.b.x1 - q.b.x0 < q.b.y1 - q.b.y0);
  const horizontals = boxes.filter((q) => q.b.y1 - q.b.y0 < q.b.x1 - q.b.x0);
  const l = pick(verticals.filter((q) => Math.abs(q.b.x1 - r.x0) <= tol && overlapY(q.b)));
  const rr = pick(verticals.filter((q) => Math.abs(q.b.x0 - r.x1) <= tol && overlapY(q.b)));
  const bb = pick(horizontals.filter((q) => Math.abs(q.b.y1 - r.y0) <= tol && overlapX(q.b)));
  const tt = pick(horizontals.filter((q) => Math.abs(q.b.y0 - r.y1) <= tol && overlapX(q.b)));
  const byId = (e: Edge) => boxes.find((q) => q.id === e.id)?.b;
  const fill = (e: Edge, side: 'l' | 'r' | 'b' | 't'): Edge => {
    const b = byId(e);
    if (!b) return e;
    const t = side === 'l' || side === 'r' ? b.x1 - b.x0 : b.y1 - b.y0;
    // outer: no opening on the far side of the board (the overlay may cover it to its outer edge). Any opening
    // beyond it along the front's edge makes it shared, even one offset from the front (a brick-bond row above)
    const alongY = (o: Rect) => Math.min(o.y1, r.y1) - Math.max(o.y0, r.y0) > tol;
    const alongX = (o: Rect) => Math.min(o.x1, r.x1) - Math.max(o.x0, r.x0) > tol;
    const far = (o: Rect) => side === 'l' ? Math.abs(o.x1 - b.x0) <= tol && alongY(o) : side === 'r' ? Math.abs(o.x0 - b.x1) <= tol && alongY(o)
      : side === 'b' ? Math.abs(o.y1 - b.y0) <= tol && alongX(o) : Math.abs(o.y0 - b.y1) <= tol && alongX(o);
    const outer = !openings.some(far);
    return { ...e, t, outer };
  };
  return { l: fill(l, 'l'), r: fill(rr, 'r'), b: fill(bb, 'b'), t: fill(tt, 't') };
}

/** Hinge count for a door height (mm). */
export const hingeCount = (hMm: number, p: FrontsProfile = FRONTS_PROFILE) => p.hinges.bands.find((b) => hMm <= b.upToMm)?.count ?? 4;
/** The longest runner that fits an inner depth (mm), or null. */
export const runnerFor = (innerMm: number, p: FrontsProfile = FRONTS_PROFILE) => p.runners.lengthsMm.filter((l) => l <= innerMm).sort((a, b) => b - a)[0] ?? null;

export function buildFronts(x: FrontsInput): FrontsOutput {
  const p = x.profile ?? FRONTS_PROFILE;
  const out: FrontsOutput = { boards: [], joints: [], hardware: [], errors: [], warnings: [], depthAdd: 0, covered: {}, specs: [] };
  if (!x.specs.length) return out;
  const { D, tf } = x;
  const reveal = T(p.overlayRevealMm), gap = T(p.insetGapMm);
  const boxes = x.parts.filter((q) => !FRONT_ROLES.has(q.role)).map((q) => ({ id: q.id, role: q.role, b: { x0: T(q.placement.x), x1: T(q.placement.x + q.placement.sx), y0: T(q.placement.y), y1: T(q.placement.y + q.placement.sy), z0: T(q.placement.z), z1: T(q.placement.z + q.placement.sz) } }));
  // behind the opening's face: a back panel, or a back block standing at the back of a tier (it does not reach the front)
  const behind = (q: (typeof boxes)[number]) => /^back|^cellback/.test(q.id) || q.role === 'back' || q.b.z1 < D - 6;
  /** How far the parts behind an opening come forward (ticks): the back, or a back block in the opening. */
  const backZOf = (r: Rect) => Math.max(0, ...boxes.filter((q) => behind(q) && Math.min(q.b.x1, r.x1) - Math.max(q.b.x0, r.x0) > 1 && Math.min(q.b.y1, r.y1) - Math.max(q.b.y0, r.y0) > 1).map((q) => q.b.z1));
  // an opening's bounds can run under a board standing in it (a staggered end panel): clip to the clear space;
  // a part behind the face (a back, a back block) does not narrow it
  const clip = (r: Rect): Rect => {
    const c = { ...r };
    for (const q of boxes) {
      if (behind(q)) continue;
      const b = q.b;
      const ox = Math.min(b.x1, c.x1) - Math.max(b.x0, c.x0), oy = Math.min(b.y1, c.y1) - Math.max(b.y0, c.y0);
      if (ox <= 1 || oy <= 1) continue;
      const vertical = b.x1 - b.x0 < b.y1 - b.y0;
      if (vertical && oy > (c.y1 - c.y0) * 0.5) { if ((b.x0 + b.x1) / 2 < (c.x0 + c.x1) / 2) c.x0 = Math.max(c.x0, b.x1); else c.x1 = Math.min(c.x1, b.x0); }
      else if (!vertical && ox > (c.x1 - c.x0) * 0.5) { if ((b.y0 + b.y1) / 2 < (c.y0 + c.y1) / 2) c.y0 = Math.max(c.y0, b.y1); else c.y1 = Math.min(c.y1, b.y0); }
    }
    return c;
  };
  const ops = new Map(x.openings.map((o) => [o.id, { ...o, r: clip({ x0: T(o.bounds.x0), x1: T(o.bounds.x1), y0: T(o.bounds.y0), y1: T(o.bounds.y1) }) }]));
  const all = x.specs.map((s) => FrontSpecSchema.parse(s));
  const used = new Set<string>();
  const hw = { cup: [] as Hardware['at'], butt: [] as Hardware['at'], knob: [] as Hardware['at'], bar: [] as Hardware['at'], runner: [] as Hardware['at'], track: [] as Hardware['at'] };
  const runnerLens: number[] = [];
  const fixFronts = (next: Spec[]) => ({ fronts: next });
  const others = (i: number) => all.filter((_s, j) => j !== i);

  all.forEach((spec, i) => {
    const known = spec.openings.filter((id) => ops.has(id));
    if (known.length < spec.openings.length) out.warnings.push({ field: 'fronts', message: `A ${spec.kind === 'drawers' ? 'drawer' : spec.kind === 'sliding' ? 'sliding door' : 'door'} named ${plural(spec.openings.length - known.length, 'opening')} that no longer exist${spec.openings.length - known.length === 1 ? 's' : ''}; ${known.length ? 'it covers the rest' : 'it was left out'}.` });
    if (!known.length) return;
    const clash = known.find((id) => used.has(id));
    if (clash) { out.errors.push({ field: 'fronts', message: `${ops.get(clash)!.label} already has a front.`, fix: { label: 'Remove the second front', patch: fixFronts(others(i)) } }); return; }
    const anchor = ops.get(known[0]!)!;
    const rects = known.map((id) => ops.get(id)!.r);
    const R0: Rect = { x0: Math.min(...rects.map((r) => r.x0)), x1: Math.max(...rects.map((r) => r.x1)), y0: Math.min(...rects.map((r) => r.y0)), y1: Math.max(...rects.map((r) => r.y1)) };
    // a span must be a full rectangle: every opening inside the box is covered, nothing else
    if (known.length > 1) {
      const inside = [...ops.values()].filter((o) => o.r.x0 >= R0.x0 - 6 && o.r.x1 <= R0.x1 + 6 && o.r.y0 >= R0.y0 - 6 && o.r.y1 <= R0.y1 + 6).map((o) => o.id);
      const area = rects.reduce((s, r) => s + (r.x1 - r.x0) * (r.y1 - r.y0), 0);
      const boards = boxes.filter((q) => q.b.x0 >= R0.x0 - 6 && q.b.x1 <= R0.x1 + 6 && q.b.y0 >= R0.y0 - 6 && q.b.y1 <= R0.y1 + 6).reduce((s, q) => s + (q.b.x1 - q.b.x0) * (q.b.y1 - q.b.y0), 0);
      const holes = inside.some((id) => !known.includes(id)) || area + boards < (R0.x1 - R0.x0) * (R0.y1 - R0.y0) * 0.97;
      const crossesRows = spec.kind === 'sliding' && rects.some((r) => Math.abs(r.y0 - rects[0]!.y0) > 6 || Math.abs(r.y1 - rects[0]!.y1) > 6);
      if (spec.kind === 'drawers' || holes || crossesRows) {
        out.errors.push({ field: 'fronts', message: spec.kind === 'drawers' ? 'A drawer stack fills one opening.' : crossesRows ? 'Sliding doors cover one horizontal run of openings.' : `The front over ${anchor.label} must cover a full rectangle of openings.`, fix: { label: 'Split the span', patch: fixFronts(all.map((s, j) => (j === i ? { ...s, openings: [known[0]!] } : s))) } });
        return;
      }
    }
    known.forEach((id) => { used.add(id); out.covered[id] = spec.kind; });
    out.specs.push({ ...spec, openings: known });
    const key = anchor.id;
    const E = edgesOf(R0, boxes, [...ops.values()].map((o) => o.r), D);
    const grow = (e: Edge) => (!e.id ? 0 : e.outer ? e.t - reveal : Math.round((e.t - reveal) / 2));
    const overlay = spec.mount === 'overlay' && spec.kind !== 'sliding';
    const F: Rect = overlay ? { x0: R0.x0 - grow(E.l), x1: R0.x1 + grow(E.r), y0: R0.y0 - grow(E.b), y1: R0.y1 + grow(E.t) } : { x0: R0.x0 + gap, x1: R0.x1 - gap, y0: R0.y0 + gap, y1: R0.y1 - gap };
    const fz0 = overlay ? D : D - tf;
    if (overlay) out.depthAdd = Math.max(out.depthAdd, tf);
    const fw = F.x1 - F.x0, fh = F.y1 - F.y0;
    const mm = (v: number) => v / 10;
    const label = known.length > 1 ? `${anchor.label} (and ${plural(known.length - 1, 'more opening')})` : anchor.label;

    if (spec.kind === 'door' || spec.kind === 'glazed') {
      const leaves = spec.hinge === 'pair' ? 2 : 1;
      const hingeSides: ('l' | 'r')[] = leaves === 2 ? ['l', 'r'] : [spec.hinge === 'right' ? 'r' : 'l'];
      // the hinge side needs a board to hang from (a staggered tier can be open at one end)
      for (const s of hingeSides) {
        if (E[s].id) continue;
        const other = s === 'l' ? 'right' : 'left';
        const can = !!E[s === 'l' ? 'r' : 'l'].id && leaves === 1;
        out.errors.push({ field: 'fronts', message: `${label} has no board on its ${s === 'l' ? 'left' : 'right'} to hang a door from.`, fix: can ? { label: `Hinge on the ${other}`, patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, hinge: other } : x2))) } : { label: 'Use a sliding door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'sliding' as const } : x2))) } });
        return;
      }
      const leafW = leaves === 2 ? Math.floor((fw - reveal) / 2) : fw;
      for (let k = 0; k < leaves; k++) {
        const lx0 = leaves === 2 && k === 1 ? F.x1 - leafW : F.x0;
        const side = hingeSides[k]!;
        const id = leaves === 2 ? `door-${key}-${k === 0 ? 'l' : 'r'}` : `door-${key}`;
        const name = `${spec.kind === 'glazed' ? 'Glazed door' : 'Door'}, ${label}${leaves === 2 ? (k === 0 ? ', left' : ', right') : ''}`;
        const L: Rect = { x0: lx0, x1: lx0 + leafW, y0: F.y0, y1: F.y1 };
        const frameIds: string[] = [];
        if (spec.kind === 'door') {
          out.boards.push({ id, role: 'door', label: name, l: fh, w: leafW, t: tf, grainWorld: 'y', box: { x: L.x0, y: L.y0, z: fz0, sx: leafW, sy: fh, sz: tf }, stock: 'carcass', faceSide: 'out', machining: overlay ? `${hingeCount(mm(fh), p)} × 35 mm cup bores on the hinge side, ${p.hinges.cupFromEdgeMm} mm from the edge` : 'butt-hinge recesses on the hinge edge' });
          frameIds.push(id);
        } else {
          const st = T(p.glazed.stileMm), rl = T(p.glazed.railMm), rb = T(p.glazed.rebateMm);
          if (leafW - 2 * st < T(p.glazed.minClearMm) || fh - 2 * rl < T(p.glazed.minClearMm)) {
            out.errors.push({ field: 'fronts', message: `${name} is too small for a frame and glass (at least ${p.glazed.minClearMm} mm of glass each way).`, fix: { label: 'Use a solid door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'door' as const } : x2))) } });
            return;
          }
          const sl = `${id}-stile-l`, sr = `${id}-stile-r`, rt = `${id}-rail-t`, rbm = `${id}-rail-b`, gl = `${id}-glass`;
          out.boards.push(
            { id: sl, role: 'door-stile', label: `${name}: left stile`, l: fh, w: st, t: tf, grainWorld: 'y', box: { x: L.x0, y: L.y0, z: fz0, sx: st, sy: fh, sz: tf }, stock: 'carcass', faceSide: 'out', machining: `mortised for two rails; ${p.glazed.rebateMm} mm glass rebate` },
            { id: sr, role: 'door-stile', label: `${name}: right stile`, l: fh, w: st, t: tf, grainWorld: 'y', box: { x: L.x1 - st, y: L.y0, z: fz0, sx: st, sy: fh, sz: tf }, stock: 'carcass', faceSide: 'out', machining: `mortised for two rails; ${p.glazed.rebateMm} mm glass rebate` },
            { id: rt, role: 'door-rail', label: `${name}: top rail`, l: leafW - 2 * st, w: rl, t: tf, grainWorld: 'x', box: { x: L.x0 + st, y: L.y1 - rl, z: fz0, sx: leafW - 2 * st, sy: rl, sz: tf }, stock: 'carcass', faceSide: 'out', machining: `tenoned into both stiles; ${p.glazed.rebateMm} mm glass rebate` },
            { id: rbm, role: 'door-rail', label: `${name}: bottom rail`, l: leafW - 2 * st, w: rl, t: tf, grainWorld: 'x', box: { x: L.x0 + st, y: L.y0, z: fz0, sx: leafW - 2 * st, sy: rl, sz: tf }, stock: 'carcass', faceSide: 'out', machining: `tenoned into both stiles; ${p.glazed.rebateMm} mm glass rebate` },
          );
          const gT = T(p.glazed.glassThicknessMm);
          const gw = leafW - 2 * st + 2 * rb - 20, gh = fh - 2 * rl + 2 * rb - 20;
          out.boards.push({ id: gl, role: 'glass', label: `${name}: glass`, l: Math.max(gw, gh), w: Math.min(gw, gh), t: gT, grainWorld: gh >= gw ? 'y' : 'x', box: { x: L.x0 + st - rb + 10, y: L.y0 + rl - rb + 10, z: fz0 + Math.round((tf - gT) / 2), sx: gw, sy: gh, sz: gT }, stock: 'glass', machining: p.glazed.glass });
          for (const r of [rt, rbm]) for (const s of [sl, sr]) out.joints.push({ a: r, b: s, kind: 'mortise-tenon', face: 'inner', datum: r === rt ? L.y1 - rl : L.y0 });
          for (const m of [sl, sr, rt, rbm]) out.joints.push({ a: gl, b: m, kind: 'rebate', face: 'inner', datum: L.y0 });
          frameIds.push(side === 'l' ? sl : sr);
        }
        // hinges: count by height, spaced from the ends; each one a joint (its bore centre is the datum)
        const n = hingeCount(mm(fh), p);
        const ends = T(p.hinges.fromEndMm);
        const ys = Array.from({ length: n }, (_x, j) => L.y0 + ends + Math.round(((fh - 2 * ends) * j) / Math.max(1, n - 1)));
        const hingeBoard = E[side].id!;
        const hingeEdgeX = side === 'l' ? L.x0 : L.x1;
        const at = side === 'l' ? hingeEdgeX + T(p.hinges.cupFromEdgeMm + p.hinges.cupBoreMm / 2) : hingeEdgeX - T(p.hinges.cupFromEdgeMm + p.hinges.cupBoreMm / 2);
        ys.forEach((y, j) => {
          out.joints.push({ a: frameIds[0]!, b: hingeBoard, kind: 'hinge', face: `h${j + 1}`, datum: y });
          (overlay ? hw.cup : hw.butt).push({ partId: frameIds[0]!, x: mm(at), y: mm(y), z: mm(fz0) });
        });
        // the pull: on the free edge, at mid height (low on a tall upper door, high on a low door)
        if (spec.pull !== 'none') {
          const freeX = side === 'l' ? L.x1 - T(p.pulls.fromEdgeMm) : L.x0 + T(p.pulls.fromEdgeMm);
          const py = fh > 9000 ? L.y0 + Math.min(fh / 2, 10000) : (L.y0 + L.y1) / 2;
          const pid = spec.kind === 'glazed' ? (side === 'l' ? `${id}-stile-r` : `${id}-stile-l`) : id;
          hw[spec.pull].push({ partId: pid, x: mm(freeX), y: mm(py), z: mm(fz0 + tf) });
        }
      }
      return;
    }

    if (spec.kind === 'sliding') {
      // two panels on a surface track in front of the carcass, overlapping in the middle; needs boards above and below
      if (!E.b.id || !E.t.id) {
        out.errors.push({ field: 'fronts', message: `Sliding doors over ${label} need a board above and below for the track.`, fix: { label: 'Use a door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'door' as const, hinge: 'pair' as const } : x2))) } });
        return;
      }
      const ov = T(p.sliding.overlapMm), tg = T(p.sliding.trackGapMm);
      const S: Rect = { x0: R0.x0 - grow(E.l), x1: R0.x1 + grow(E.r), y0: R0.y0 - grow(E.b), y1: R0.y1 + grow(E.t) };
      const pw = Math.ceil((S.x1 - S.x0 + ov) / 2), ph = S.y1 - S.y0;
      for (let k = 0; k < 2; k++) {
        const id = `slider-${key}-${k + 1}`;
        const z0 = D + tg + k * (tf + tg);
        const x0 = k === 0 ? S.x0 : S.x1 - pw;
        out.boards.push({ id, role: 'slider', label: `Sliding door ${k + 1}, ${label}`, l: ph, w: pw, t: tf, grainWorld: 'y', box: { x: x0, y: S.y0, z: z0, sx: pw, sy: ph, sz: tf }, stock: 'carcass', faceSide: 'out', machining: 'grooved top and bottom for the track guides' });
        out.joints.push({ a: id, b: E.b.id, kind: 'track', face: 'top', datum: S.y0 });
        if (spec.pull !== 'none') hw[spec.pull].push({ partId: id, x: mm(k === 0 ? x0 + T(p.pulls.fromEdgeMm) : x0 + pw - T(p.pulls.fromEdgeMm)), y: mm(S.y0 + ph / 2), z: mm(z0 + tf) });
      }
      hw.track.push({ partId: E.b.id, x: mm((S.x0 + S.x1) / 2), y: mm(S.y0), z: mm(D) }, { partId: E.t.id, x: mm((S.x0 + S.x1) / 2), y: mm(S.y1), z: mm(D) });
      out.depthAdd = Math.max(out.depthAdd, 2 * tf + 2 * tg);
      return;
    }

    // drawers: n stacked fronts over the opening, each with a box behind it on side runners
    const n = Math.max(1, Math.min(4, spec.drawers ?? 1));
    const ratios = spec.drawerRatios && spec.drawerRatios.length === n ? spec.drawerRatios : Array.from({ length: n }, () => 1);
    const sumR = ratios.reduce((s, r) => s + r, 0);
    const innerDepth = (overlay ? D : D - tf) - backZOf(anchor.r);
    // a runner needs 10 mm clear behind it, so the depth a drawer needs is the shortest runner plus 10 (or the profile's floor)
    const runner = runnerFor(mm(innerDepth) - 10, p);
    const needMm = Math.max(p.drawerBox.minInnerDepthMm, Math.min(...p.runners.lengthsMm) + 10);
    if (!runner || mm(innerDepth) < p.drawerBox.minInnerDepthMm) {
      out.errors.push({ field: 'fronts', message: `${label} is ${Math.floor(mm(innerDepth))} mm deep inside; a drawer needs at least ${needMm} mm for its runners.`, fix: { label: 'Use a door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'door' as const, drawers: undefined, hinge: (R0.x1 - R0.x0) > 6000 ? 'pair' as const : 'left' as const } : x2))) } });
      return;
    }
    if (!E.l.id || !E.r.id) {
      out.errors.push({ field: 'fronts', message: `${label} needs a board on both sides for the drawer runners.`, fix: { label: 'Use a door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'door' as const, drawers: undefined, hinge: E.l.id ? 'left' as const : 'right' as const } : x2))) } });
      return;
    }
    runnerLens.push(runner);
    const bt = T(p.drawerBox.thicknessMm), clr = T(p.runners.clearanceMm), bT = x.backT || 60;
    const R = anchor.r;
    const boxW = R.x1 - R.x0 - 2 * clr;
    const boxD = T(runner);
    let fy = F.y0, oy = R.y0;
    const frontsH = fh - (n - 1) * reveal;
    const clearH = R.y1 - R.y0;
    ratios.forEach((r, k) => {
      const hF = k === n - 1 ? F.y1 - fy : Math.round((frontsH * r) / sumR);
      const hO = k === n - 1 ? R.y1 - oy : Math.round((clearH * r) / sumR);
      const base = `drawer-${key}-${k + 1}`;
      const name = `Drawer ${k + 1}${n > 1 ? ` of ${n}` : ''}, ${label}`;
      const boxH = hO - T(p.drawerBox.topClearanceMm) - T(p.drawerBox.bottomClearanceMm);
      if (boxH < T(p.drawerBox.minHeightMm)) {
        out.errors.push({ field: 'fronts', message: `${name} would be ${Math.round(mm(boxH))} mm high inside; at least ${p.drawerBox.minHeightMm} mm.`, fix: n > 1 ? { label: 'One drawer fewer', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, drawers: n - 1, drawerRatios: undefined } : x2))) } : { label: 'Use a door', patch: fixFronts(all.map((x2, j) => (j === i ? { ...x2, kind: 'door' as const, drawers: undefined } : x2))) } });
        fy += hF + reveal; oy += hO;
        return;
      }
      const by0 = oy + T(p.drawerBox.bottomClearanceMm);
      const bz1 = fz0, bz0 = fz0 - boxD;
      const bx0 = R.x0 + clr;
      out.boards.push(
        { id: `${base}-front`, role: 'drawer-front', label: `${name}: front`, l: fw, w: hF, t: tf, grainWorld: 'x', box: { x: F.x0, y: fy, z: fz0, sx: fw, sy: hF, sz: tf }, stock: 'carcass', faceSide: 'out', machining: 'applied to the box with four screws from inside' },
        { id: `${base}-side-l`, role: 'drawer-side', label: `${name}: left side`, l: boxD, w: boxH, t: bt, grainWorld: 'z', box: { x: bx0, y: by0, z: bz0, sx: bt, sy: boxH, sz: boxD }, stock: 'drawer', machining: 'grooved 6 mm for the bottom; runner screwed on the outside' },
        { id: `${base}-side-r`, role: 'drawer-side', label: `${name}: right side`, l: boxD, w: boxH, t: bt, grainWorld: 'z', box: { x: bx0 + boxW - bt, y: by0, z: bz0, sx: bt, sy: boxH, sz: boxD }, stock: 'drawer', machining: 'grooved 6 mm for the bottom; runner screwed on the outside' },
        { id: `${base}-rear`, role: 'drawer-rear', label: `${name}: back`, l: boxW - 2 * bt, w: boxH, t: bt, grainWorld: 'x', box: { x: bx0 + bt, y: by0, z: bz0, sx: boxW - 2 * bt, sy: boxH, sz: bt }, stock: 'drawer' },
        { id: `${base}-sub`, role: 'drawer-rear', label: `${name}: inner front`, l: boxW - 2 * bt, w: boxH, t: bt, grainWorld: 'x', box: { x: bx0 + bt, y: by0, z: bz1 - bt, sx: boxW - 2 * bt, sy: boxH, sz: bt }, stock: 'drawer' },
        { id: `${base}-bottom`, role: 'drawer-bottom', label: `${name}: bottom`, l: boxW - 2 * bt, w: boxD - 2 * bt, t: bT, grainWorld: 'x', box: { x: bx0 + bt, y: by0, z: bz0 + bt, sx: boxW - 2 * bt, sy: bT, sz: boxD - 2 * bt }, stock: 'back' },
      );
      for (const s of ['side-l', 'side-r']) {
        out.joints.push({ a: `${base}-rear`, b: `${base}-${s}`, kind: 'screwed-butt', face: 'inner', datum: by0 });
        out.joints.push({ a: `${base}-sub`, b: `${base}-${s}`, kind: 'screwed-butt', face: 'inner', datum: by0 });
        out.joints.push({ a: `${base}-bottom`, b: `${base}-${s}`, kind: 'housed-dado', face: 'inner', datum: by0 });
      }
      out.joints.push({ a: `${base}-front`, b: `${base}-sub`, kind: 'screwed-butt', face: 'front', datum: fy });
      // each side runs on a runner fixed to the board beside it
      if (E.l.id) out.joints.push({ a: `${base}-side-l`, b: E.l.id, kind: 'runner', face: 'inner', datum: by0 + boxH / 2 });
      if (E.r.id) out.joints.push({ a: `${base}-side-r`, b: E.r.id, kind: 'runner', face: 'inner', datum: by0 + boxH / 2 });
      hw.runner.push({ partId: `${base}-side-l`, x: mm(bx0), y: mm(by0 + boxH / 2), z: mm((bz0 + bz1) / 2) }, { partId: `${base}-side-r`, x: mm(bx0 + boxW), y: mm(by0 + boxH / 2), z: mm((bz0 + bz1) / 2) });
      if (spec.pull !== 'none') hw[spec.pull].push({ partId: `${base}-front`, x: mm((F.x0 + F.x1) / 2), y: mm(fy + hF / 2), z: mm(fz0 + tf) });
      fy += hF + reveal; oy += hO;
    });
  });

  const add = (kind: string, at: Hardware['at'], spec: string, visible: boolean) => { if (at.length) out.hardware.push({ kind, qty: at.length, at, visible, spec }); };
  add('cup-hinge', hw.cup, p.hinges.cup, false);
  add('butt-hinge', hw.butt, p.hinges.butt, false);
  add('drawer-runner', hw.runner, `${p.runners.spec}; ${[...new Set(runnerLens)].sort((a, b) => a - b).map((l) => `${l} mm`).join(', ')}`, false);
  add('sliding-track', hw.track, p.sliding.track, false);
  add('knob', hw.knob, p.pulls.knob, true);
  add('bar-handle', hw.bar, p.pulls.bar, true);
  return out;
}

/** The Shape control for doors and drawers (every family; src/components/FrontsControl.vue). */
export function frontsParameterDef(group = 'Doors and drawers'): ParameterDef {
  return { key: 'fronts', type: 'list', default: null, critical: false, ui: { label: 'Doors and drawers', help: 'Pick one opening, or several that make a rectangle, then choose what covers them.', control: 'fronts', group } };
}

/** Drop fronts naming openings a design no longer has (a row or cell count changed), keeping the rest. */
export function pruneFronts(specs: readonly FrontSpec[], openingIds: ReadonlySet<string>): FrontSpec[] {
  return specs.map((s) => ({ ...s, openings: s.openings.filter((id) => openingIds.has(id)) })).filter((s) => s.openings.length > 0);
}
